/**
 * KitchenTicketPreview - Renders a kitchen order ticket for printing
 * Large order number, items with modifications, customer name
 */
import { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '@/utils/apiClient';

// Escape HTML to prevent XSS
const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export default function KitchenTicketPreview({ orderId, isReprint = false, isCancelled = false, onClose }) {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchTicket = async () => {
    try {
      const res = await api.get(`/receipts/kitchen/${orderId}?is_reprint=${isReprint}&is_cancelled=${isCancelled}`);
      setTicket(res.data);
    } catch (err) {
      console.error('Failed to fetch kitchen ticket');
    }
    setLoading(false);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    // Get sanitized innerHTML (React already escapes content)
    const safeContent = printContent.innerHTML;
    const safeOrderNumber = escapeHtml(ticket?.order_number);
    
    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kitchen Ticket - ${safeOrderNumber}</title>
          <style>
            @page { 
              size: 80mm auto; 
              margin: 0; 
            }
            body { 
              font-family: 'Courier New', monospace;
              font-size: 12px;
              width: 80mm;
              margin: 0;
              padding: 8mm;
              background: white;
              color: black;
            }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .text-3xl { font-size: 32px; }
            .text-xl { font-size: 18px; }
            .text-lg { font-size: 14px; }
            .text-sm { font-size: 10px; }
            .border-dashed {
              border-bottom: 1px dashed #000;
              padding-bottom: 8px;
              margin-bottom: 8px;
            }
            .border-solid {
              border-bottom: 2px solid #000;
              padding-bottom: 8px;
              margin-bottom: 8px;
            }
            .mb-1 { margin-bottom: 4px; }
            .mb-2 { margin-bottom: 8px; }
            .mt-2 { margin-top: 8px; }
            .ml-4 { margin-left: 16px; }
            .cancelled-banner {
              background: #000;
              color: #fff;
              padding: 8px;
              text-align: center;
              font-weight: bold;
              font-size: 18px;
              margin-bottom: 8px;
              border: 3px solid #000;
            }
            .reprint-banner {
              background: #666;
              color: #fff;
              padding: 4px;
              text-align: center;
              font-weight: bold;
              margin-bottom: 8px;
            }
            .item {
              margin-bottom: 12px;
              padding-bottom: 8px;
              border-bottom: 1px dotted #666;
            }
            .item:last-child {
              border-bottom: none;
            }
            .modification {
              margin-left: 16px;
              font-weight: bold;
            }
            .special-note {
              margin-left: 16px;
              font-style: italic;
              background: #eee;
              padding: 2px 4px;
            }
          </style>
        </head>
        <body>
          ${safeContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="text-center py-8 text-muted-foreground">Ticket not found</div>;
  }

  return (
    <div className="space-y-4">
      {/* Print Preview */}
      <div 
        ref={printRef}
        className="bg-white text-black p-6 rounded-lg shadow-lg max-w-[300px] mx-auto font-mono text-xs"
        data-testid="kitchen-ticket-preview"
      >
        {/* Cancelled Banner */}
        {ticket.is_cancelled && (
          <div className="cancelled-banner bg-black text-white p-2 text-center font-bold text-lg mb-4 border-4 border-black">
            *** CANCELLED ***
          </div>
        )}

        {/* Reprint Banner */}
        {ticket.is_reprint && !ticket.is_cancelled && (
          <div className="reprint-banner bg-gray-600 text-white p-1 text-center font-bold mb-4">
            *** REPRINT ***
          </div>
        )}

        {/* Header with LARGE Order Number */}
        <div className="text-center border-b-4 border-black pb-4 mb-4">
          <p className="text-sm font-bold">KITCHEN ORDER</p>
          <p className="text-4xl font-bold my-2" style={{ fontSize: '36px', letterSpacing: '2px' }}>
            #{ticket.order_number}
          </p>
          <div className="flex justify-between text-sm mt-2">
            <span>{ticket.date}</span>
            <span className="font-bold">{ticket.time_ordered}</span>
          </div>
        </div>

        {/* Customer Name (for pickup) */}
        <div className="border-b-2 border-black pb-3 mb-4">
          <div className="text-center">
            <p className="text-xs text-gray-600">CUSTOMER NAME:</p>
            <p className="text-xl font-bold">{ticket.customer_name || 'Guest'}</p>
          </div>
          {ticket.pickup_time && (
            <div className="text-center mt-2">
              <p className="text-xs text-gray-600">PICKUP TIME:</p>
              <p className="font-bold">{ticket.pickup_time}</p>
            </div>
          )}
          {ticket.estimated_ready && (
            <div className="text-center mt-1 text-sm">
              <span className="text-gray-600">Est. Ready: </span>
              <span className="font-bold">{ticket.estimated_ready}</span>
            </div>
          )}
        </div>

        {/* Items with Modifications */}
        <div className="mb-4">
          <p className="font-bold text-center mb-3 border-b border-gray-400 pb-1">ITEMS</p>
          {ticket.items.map((item, idx) => (
            <div key={idx} className="item mb-4 pb-3 border-b border-dotted border-gray-400 last:border-0">
              <div className="flex items-start gap-2">
                <span className="font-bold text-lg">{item.quantity}x</span>
                <div className="flex-1">
                  <p className="font-bold text-sm">
                    {item.name}
                    {item.size && <span className="ml-1">({item.size})</span>}
                  </p>
                  
                  {/* Modifications */}
                  {item.customizations && item.customizations.length > 0 && (
                    <div className="ml-2 mt-1">
                      {item.customizations.map((mod, i) => (
                        <p key={i} className="font-bold text-sm">
                          → {mod}
                        </p>
                      ))}
                    </div>
                  )}
                  
                  {/* Special Instructions */}
                  {item.special_instructions && (
                    <div className="mt-1 p-1 bg-gray-200">
                      <p className="text-xs italic">
                        NOTE: {item.special_instructions}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order-level Notes */}
        {ticket.order_notes && (
          <div className="border-t-2 border-black pt-3 mb-4">
            <p className="font-bold text-center">ORDER NOTES:</p>
            <p className="text-center p-2 bg-gray-200 mt-1">
              {ticket.order_notes}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center border-t-2 border-black pt-3 mt-4">
          <p className="text-xs text-gray-600">
            Order Type: {ticket.order_type?.toUpperCase() || 'PICKUP'}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            Printed: {new Date().toLocaleTimeString()}
          </p>
        </div>
      </div>

      {/* Print Button */}
      <div className="flex justify-center gap-3">
        <button
          onClick={handlePrint}
          className={`px-6 py-2 rounded-lg font-medium ${
            ticket.is_cancelled 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-accent hover:bg-accent/90 text-white'
          }`}
          data-testid="print-kitchen-ticket-btn"
        >
          {ticket.is_cancelled ? 'Print Cancellation Notice' : 'Print Kitchen Ticket'}
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg font-medium hover:bg-gray-300"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}
